import { useMemo } from "react"
import type { DataStatus } from "helpers/data-statuses"
import { isUuid } from "helpers/types/uuid"
import { useIsMultiDashboardsEnabled } from "resources/account"
import { useDashboardInstance } from "resources/dashboard-instances"
import { useLayout } from "resources/layouts"
import { readLayoutConfig, type LayoutConfigV1 } from "../contracts/layouts"

export interface BackingLayoutConfig {
  // Non-null only for a real (UUID) instance under the multi-dashboard flag — a legacy slug/"default"
  // dashboard has no backing layout to restore.
  resetInstanceId: string | null
  backingLayoutConfig: LayoutConfigV1 | null
  layoutStatus: DataStatus
}

// Resolves the layout a dashboard resets to: shared by DashboardModulesRenderer (applies the reset)
// and LayoutActionsMenu (gates the "Reset layout" menu item on the same loaded state) — both need the
// identical UUID check → instance → layout → parsed-config chain. [APP-3309]
export function useBackingLayoutConfig(dashboardId: string): BackingLayoutConfig {
  const isInstanceMode = useIsMultiDashboardsEnabled()
  const resetInstanceId = isInstanceMode && isUuid(dashboardId) ? dashboardId : null
  const [resetInstance] = useDashboardInstance(resetInstanceId)
  const [backingLayout, layoutStatus] = useLayout(
    isInstanceMode && resetInstance?.layoutId ? resetInstance.layoutId : null,
  )
  const backingLayoutConfig = useMemo<LayoutConfigV1 | null>(() => {
    if (!backingLayout) return null
    const result = readLayoutConfig(backingLayout.config)
    return result.status === "ok" ? result.config : null
  }, [backingLayout])

  return { resetInstanceId, backingLayoutConfig, layoutStatus }
}
